import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, RefreshCw, Play, FileText, LayoutGrid, Shield, Users, Calendar } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import ActivityItem from '../components/ui/ActivityItem';
import QuickNavCard from '../components/ui/QuickNavCard';
import Sidebar from '../components/ui/Sidebar';
import { useAuthContext } from '../../components/auth/AuthProvider';
import { getCamundaService } from '../../infrastructure/camunda';
import type { HistoricProcessInstance, HistoricTaskInstance } from '../../infrastructure/camunda';

interface DashboardStats {
  tasksCompleted: number;
  totalTasks: number;
  activeProcesses: number;
  openIncidents: number;
  pendingTasks: number;
}

interface RecentActivityItem {
  id: string;
  type: 'task' | 'process' | 'incident' | 'user';
  name: string;
  time: string;
  action: string;
  status: 'done' | 'active' | 'critical' | 'new';
  isNew?: boolean;
}

// Format count with 99+ for values over 99
const formatCount = (count: number): string => {
  return count > 99 ? '99+' : count.toString();
};

export const WelcomePage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuthContext();

  // Get username from OIDC user profile
  const username = user?.profile?.preferred_username || user?.profile?.name || user?.profile?.email || 'User';

  // Get time-based greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const [stats, setStats] = useState<DashboardStats>({
    tasksCompleted: 0,
    totalTasks: 0,
    activeProcesses: 0,
    openIncidents: 0,
    pendingTasks: 0,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivityItem[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const lastActivityIdsRef = useRef<Set<string>>(new Set());
  const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Filter state - days selection like Angular dashboard
  const [selectedDays, setSelectedDays] = useState<number>(7);

  // Process trends data for line chart
  const [processTrendsData, setProcessTrendsData] = useState<{ labels: string[], values: number[] }>({
    labels: [],
    values: []
  });

  // Inflight requests data (process instances by type)
  const [inflightData, setInflightData] = useState<{ labels: string[], values: number[] }>({
    labels: [],
    values: []
  });

  const camunda = getCamundaService();

  // Format relative time
  const formatRelativeTime = (dateString: string | null): string => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  // Fetch dashboard statistics
  const fetchDashboardStats = useCallback(async () => {
    try {
      setStatsLoading(true);

      const [processStats, incidents, tasks, historicTasks] = await Promise.all([
        camunda.processDefinition.getStatistics({ incidents: true }),
        camunda.incident.getList({}),
        camunda.task.getList({ maxResults: 200 }),
        camunda.history.getTaskInstances({ finished: true, maxResults: 200 }),
      ]);

      const activeProcesses = processStats.reduce((sum: number, stat: { instances: number }) => sum + stat.instances, 0);

      setStats({
        tasksCompleted: historicTasks.length,
        totalTasks: tasks.length + historicTasks.length,
        activeProcesses,
        openIncidents: incidents.length,
        pendingTasks: tasks.length,
      });

      await fetchRecentActivity(true);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    } finally {
      setStatsLoading(false);
    }
  }, [camunda]);

  // Fetch recent activity
  const fetchRecentActivity = useCallback(async (isInitial = false) => {
    try {
      if (!isInitial) setIsPolling(true);

      const [recentInstances, historicTasks] = await Promise.all([
        camunda.history.getProcessInstances({
          sortBy: 'startTime',
          sortOrder: 'desc',
          maxResults: 4,
        }),
        camunda.history.getTaskInstances({ finished: true, maxResults: 2 }),
      ]);

      const activityItems: RecentActivityItem[] = [];
      const currentIds = new Set<string>();

      recentInstances.forEach((instance: HistoricProcessInstance) => {
        const id = `process-${instance.id}`;
        currentIds.add(id);
        const processName = instance.processDefinitionName || instance.processDefinitionKey || 'Process';
        const isCompleted = instance.state === 'COMPLETED';
        const isCanceled = instance.state === 'EXTERNALLY_TERMINATED' || instance.state === 'INTERNALLY_TERMINATED';
        const isNew = !isInitial && !lastActivityIdsRef.current.has(id);

        activityItems.push({
          id,
          type: 'process',
          name: processName,
          time: formatRelativeTime(instance.startTime),
          action: isCompleted ? `Completed ${processName}` : isCanceled ? `Canceled ${processName}` : `Started ${processName}`,
          status: isCompleted ? 'done' : isCanceled ? 'critical' : 'active',
          isNew,
        });
      });

      historicTasks.forEach((task: HistoricTaskInstance) => {
        const id = `task-${task.id}`;
        currentIds.add(id);
        const isNew = !isInitial && !lastActivityIdsRef.current.has(id);

        activityItems.push({
          id,
          type: 'task',
          name: task.assignee || 'System',
          time: formatRelativeTime(task.endTime),
          action: `Completed ${task.name || 'Task'}`,
          status: 'done',
          isNew,
        });
      });

      lastActivityIdsRef.current = currentIds;
      setRecentActivity(activityItems.slice(0, 4));
    } catch (error) {
      console.error('Error fetching recent activity:', error);
    } finally {
      setIsPolling(false);
    }
  }, [camunda]);

  // Fetch process trends based on selected days
  // Format date for Camunda API (Java SimpleDateFormat compatible)
  const formatDateForCamunda = (date: Date): string => {
    const pad = (num: number) => String(num).padStart(2, '0');
    const year = date.getFullYear();
    const month = pad(date.getMonth() + 1);
    const day = pad(date.getDate());
    const hours = pad(date.getHours());
    const minutes = pad(date.getMinutes());
    const seconds = pad(date.getSeconds());
    const ms = String(date.getMilliseconds()).padStart(3, '0');

    // Get timezone offset in format +HHMM or -HHMM
    const tzOffset = -date.getTimezoneOffset();
    const tzSign = tzOffset >= 0 ? '+' : '-';
    const tzHours = pad(Math.floor(Math.abs(tzOffset) / 60));
    const tzMinutes = pad(Math.abs(tzOffset) % 60);

    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${ms}${tzSign}${tzHours}${tzMinutes}`;
  };

  const fetchProcessTrends = useCallback(async () => {
    try {
      // Calculate date range
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + 1); // Tomorrow start to include today
      endDate.setHours(23, 59, 59, 999);

      const startDate = new Date();
      startDate.setDate(startDate.getDate() - selectedDays);
      startDate.setHours(0, 0, 0, 0);

      const formattedStartDate = formatDateForCamunda(startDate);
      const formattedEndDate = formatDateForCamunda(endDate);

      // Get historic process instances
      const historicInstances = await camunda.history.getProcessInstances({
        startedAfter: formattedStartDate,
        startedBefore: formattedEndDate,
        maxResults: 1000
      });

      // Group by date - create entries for all days in range
      const trends: { [date: string]: number } = {};
      const currentDate = new Date(startDate);
      const today = new Date();
      today.setHours(23, 59, 59, 999);

      while (currentDate <= today) {
        const key = currentDate.toISOString().slice(0, 10);
        trends[key] = 0;
        currentDate.setDate(currentDate.getDate() + 1);
      }

      // Count instances per day
      historicInstances.forEach((instance: HistoricProcessInstance) => {
        if (instance.startTime) {
          const date = instance.startTime.slice(0, 10);
          if (trends[date] !== undefined) {
            trends[date]++;
          }
        }
      });

      // Always set data even if all zeros
      const labels = Object.keys(trends).map(d => {
        const date = new Date(d);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      });
      const values = Object.values(trends);

      setProcessTrendsData({ labels, values });
    } catch (error) {
      console.error('Error fetching process trends:', error);
      // Set empty data with labels for the date range
      const labels = [];
      const values = [];
      for (let i = selectedDays; i >= 0; i--) {
        const d = new Date();
        d.setDate(d.getDate() - i);
        labels.push(d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }));
        values.push(0);
      }
      setProcessTrendsData({ labels, values });
    }
  }, [camunda, selectedDays]);

  // Fetch inflight requests (active process instances by type)
  const fetchInflightData = useCallback(async () => {
    try {
      const processStats = await camunda.processDefinition.getStatistics({ incidents: true });

      const labels: string[] = [];
      const values: number[] = [];

      processStats.forEach((stat: { definition?: { key?: string }, instances: number }) => {
        if (stat.instances > 0) {
          labels.push(stat.definition?.key || 'Unknown');
          values.push(stat.instances);
        }
      });

      // If no data, add placeholder
      if (labels.length === 0) {
        labels.push('No Processes');
        values.push(0);
      }

      setInflightData({ labels, values });
    } catch (error) {
      console.error('Error fetching inflight data:', error);
    }
  }, [camunda]);

  React.useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, isLoading, navigate]);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      fetchDashboardStats();
      fetchProcessTrends();
      fetchInflightData();
    }
  }, [isAuthenticated, isLoading, fetchDashboardStats, fetchProcessTrends, fetchInflightData]);

  // Re-fetch trends when selectedDays changes
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      fetchProcessTrends();
    }
  }, [selectedDays, isAuthenticated, isLoading, fetchProcessTrends]);

  useEffect(() => {
    if (isAuthenticated && !isLoading && !statsLoading) {
      pollingIntervalRef.current = setInterval(() => {
        fetchRecentActivity();
      }, 10000);

      return () => {
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
        }
      };
    }
  }, [isAuthenticated, isLoading, statsLoading, fetchRecentActivity]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Colors for inflight requests donut chart
  const donutColors = ['#6366f1', '#a855f7', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#3b82f6', '#8b5cf6'];
  const inflightTotal = inflightData.values.reduce((sum, v) => sum + v, 0) || 1;

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar />

      <div className="flex-1 ml-16 overflow-auto">
        <div className="p-5">
          {/* Welcome Header */}
          <div className="relative mb-6">
            <div className="absolute right-0 top-0 flex gap-2">
              <div className="w-12 h-12 rounded-full bg-indigo-100"></div>
              <div className="w-16 h-16 rounded-full bg-indigo-200 -mt-2"></div>
              <div className="w-10 h-10 rounded-full bg-indigo-100 mt-3"></div>
            </div>

            <div className="relative z-10">
              <h1 className="text-2xl font-bold text-gray-900 mb-1">
                {getGreeting()}, <span className="text-indigo-600">{username}!</span>
              </h1>
              <p className="text-sm text-gray-600 mb-4">Welcome to DoIT, your AI-powered Business Automation Platform!</p>

              <div className="flex gap-2">
                <button className="inline-flex items-center gap-1.5 px-3 py-2 border border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-colors text-sm">
                  <Play size={14} fill="currentColor" />
                  Watch demo
                </button>
                <button
                  onClick={() => navigate('/studio')}
                  className="inline-flex items-center gap-1.5 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm"
                >
                  Create a flow
                </button>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="flex items-center justify-end gap-2 mb-5">
            <select
              value={selectedDays}
              onChange={(e) => setSelectedDays(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500"
            >
              <option value={1}>Last 1 day</option>
              <option value={3}>Last 3 days</option>
              <option value={7}>Last 7 days</option>
              <option value={15}>Last 15 days</option>
            </select>
            <button
              onClick={() => { fetchProcessTrends(); fetchInflightData(); fetchDashboardStats(); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-200 rounded-lg text-xs text-gray-600 hover:bg-gray-50"
              title="Refresh data"
            >
              <RefreshCw size={14} /> Refresh
            </button>
          </div>

          {/* Main Grid - Compact Layout */}
          <div className="grid grid-cols-12 gap-4">
            {/* Stats Cards - 2x2 grid, smaller */}
            <div className="col-span-3 grid grid-cols-2 gap-3">
              {statsLoading ? (
                <>
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="bg-white rounded-lg border border-gray-200 p-3 animate-pulse">
                      <div className="w-8 h-8 bg-gray-200 rounded-lg mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-16 mb-1"></div>
                      <div className="h-6 bg-gray-200 rounded w-10"></div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="bg-indigo-600 rounded-lg p-3 text-white">
                    <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center mb-2">
                      <LayoutGrid size={16} />
                    </div>
                    <p className="text-xs text-indigo-200 mb-0.5">Active Processes</p>
                    <span className="text-xl font-bold">{stats.activeProcesses}</span>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                      <FileText size={16} className="text-gray-600" />
                    </div>
                    <p className="text-xs text-gray-500 mb-0.5">Tasks Done</p>
                    <span className="text-xl font-bold text-gray-900">{stats.tasksCompleted}</span>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                      <Shield size={16} className="text-gray-600" />
                    </div>
                    <p className="text-xs text-gray-500 mb-0.5">Incidents</p>
                    <span className="text-xl font-bold text-gray-900">{stats.openIncidents}</span>
                  </div>

                  <div className="bg-white rounded-lg border border-gray-200 p-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center mb-2">
                      <Users size={16} className="text-gray-600" />
                    </div>
                    <p className="text-xs text-gray-500 mb-0.5">Pending</p>
                    <span className="text-xl font-bold text-gray-900">{stats.pendingTasks}</span>
                  </div>
                </>
              )}
            </div>

            {/* Process Trends - Using Recharts AreaChart */}
            <div className="col-span-5 bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-900">
                  Process Trends (Last {selectedDays} {selectedDays === 1 ? 'Day' : 'Days'})
                </h2>
              </div>
              <div className="h-44">
                {processTrendsData.values.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={processTrendsData.labels.map((label, i) => ({
                        name: label,
                        instances: processTrendsData.values[i]
                      }))}
                      margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
                    >
                      <defs>
                        <linearGradient id="colorInstances" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0.05} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="name"
                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                        axisLine={{ stroke: '#e5e7eb' }}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 10, fill: '#9ca3af' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#fff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          fontSize: '12px'
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="instances"
                        stroke="#6366f1"
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorInstances)"
                        name="Started Instances"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                    No data available
                  </div>
                )}
              </div>
            </div>

            {/* Inflight Requests - Using Recharts PieChart */}
            <div className="col-span-4 bg-white rounded-lg border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-sm font-semibold text-gray-900">Inflight Requests</h2>
                <span className="text-xl font-bold text-indigo-600">{inflightTotal}</span>
              </div>
              <div className="flex items-center gap-4">
                {/* Pie Chart - Left side */}
                <div className="w-28 h-28 flex-shrink-0">
                  {inflightData.values.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={inflightData.labels.slice(0, 8).map((label, i) => ({
                            name: label,
                            value: inflightData.values[i]
                          }))}
                          cx="50%"
                          cy="50%"
                          innerRadius={25}
                          outerRadius={45}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {inflightData.labels.slice(0, 8).map((_, index) => (
                            <Cell key={`cell-${index}`} fill={donutColors[index % donutColors.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={{
                            backgroundColor: '#fff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '8px',
                            fontSize: '12px'
                          }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 text-xs">
                      No data
                    </div>
                  )}
                </div>
                {/* Custom Legend - Right side */}
                <div className="flex-1 space-y-1 overflow-hidden max-h-28">
                  {inflightData.labels.slice(0, 5).map((label, i) => (
                    <div key={i} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <div
                          className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                          style={{ backgroundColor: donutColors[i % donutColors.length] }}
                        />
                        <span className="text-gray-600 truncate" title={label}>
                          {label.length > 12 ? label.slice(0, 12) + '...' : label}
                        </span>
                      </div>
                      <span className="font-semibold text-gray-900 ml-1">{inflightData.values[i]}</span>
                    </div>
                  ))}
                  {inflightData.labels.length > 5 && (
                    <div className="text-[10px] text-gray-400">+{inflightData.labels.length - 5} more</div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Second Row */}
          <div className="grid grid-cols-12 gap-4 mt-4">
            {/* Quick Navigation - Compact */}
            <div className="col-span-3">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Quick Access</h2>
              <div className="space-y-2">
                <QuickNavCard icon={<Activity size={16} />} title="Analytics" description="Monitor" onClick={() => navigate('/analytics')} />
                <QuickNavCard icon={<FileText size={16} />} title="Tasks" description="View all" badge={stats.pendingTasks > 0 ? formatCount(stats.pendingTasks) : undefined} onClick={() => navigate('/tasks')} />
                <QuickNavCard icon={<LayoutGrid size={16} />} title="Processes" description="Manage" onClick={() => navigate('/processes')} />
                <QuickNavCard icon={<Shield size={16} />} title="Incidents" description="Resolve" badge={stats.openIncidents > 0 ? formatCount(stats.openIncidents) : undefined} onClick={() => navigate('/incidents')} />
              </div>
            </div>

            {/* Recent Activity - Compact */}
            <div className="col-span-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Recent Activity</h2>
                  {isPolling && <RefreshCw size={12} className="text-indigo-500 animate-spin" />}
                  <span className="flex items-center gap-1 text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded-full">
                    <span className="w-1 h-1 bg-green-500 rounded-full animate-pulse"></span>
                    Live
                  </span>
                </div>
              </div>

              <div className="bg-white rounded-lg border border-gray-200 p-3">
                {statsLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-start gap-2 animate-pulse">
                        <div className="w-6 h-6 bg-gray-200 rounded-lg flex-shrink-0"></div>
                        <div className="flex-1">
                          <div className="h-3 bg-gray-200 rounded w-20 mb-1"></div>
                          <div className="h-2 bg-gray-200 rounded w-32"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : recentActivity.length === 0 ? (
                  <p className="text-xs text-gray-500 text-center py-3">No recent activity</p>
                ) : (
                  <div className="space-y-2">
                    {recentActivity.slice(0, 4).map((activity) => (
                      <div
                        key={activity.id}
                        className={`transition-all duration-500 ${activity.isNew ? 'bg-indigo-50 -mx-3 px-3 py-1 border-l-2 border-indigo-500' : ''}`}
                      >
                        <ActivityItem
                          icon={activity.type === 'process' ? <LayoutGrid size={14} /> : <FileText size={14} />}
                          name={activity.name}
                          time={activity.time}
                          action={activity.action}
                          status={activity.status}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Platform Features - Compact Row */}
            <div className="col-span-5">
              <h2 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Platform Features</h2>
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-shadow cursor-pointer group">
                  <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-indigo-600 transition-colors">
                    <Activity size={16} className="text-indigo-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">BPMN Studio</h3>
                  <p className="text-xs text-gray-500">Design business processes</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-shadow cursor-pointer group">
                  <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-purple-600 transition-colors">
                    <LayoutGrid size={16} className="text-purple-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">Process Engine</h3>
                  <p className="text-xs text-gray-500">Execute workflows</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-shadow cursor-pointer group">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-green-600 transition-colors">
                    <Shield size={16} className="text-green-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">Enterprise Security</h3>
                  <p className="text-xs text-gray-500">OIDC authentication</p>
                </div>
                <div className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-shadow cursor-pointer group">
                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mb-2 group-hover:bg-orange-600 transition-colors">
                    <Calendar size={16} className="text-orange-600 group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">Real-time Analytics</h3>
                  <p className="text-xs text-gray-500">Track performance</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
